import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { TextField, InputAdornment, Switch, styled, Divider, Autocomplete } from "@mui/material"
import { AdminPanelSettings, AccessTime } from "@mui/icons-material"
import { getPlatformPricing } from "../../app/store/slices/admin-settings/adminSettings"
import { H2, H5, Body2, Body1, Body3, H6 } from "../../themes/typography"
import "../../styles/saas-pages/Pricing.css"

function Pricing() {

    const dispatch = useDispatch()
    const platformPricingSettings = useSelector(state => state.adminSettings.platformPricing);

    //     {
    //   "success": true,
    //   "data": {
    //     "ticket-booking-fee-decider": 500,
    //     "commission-fee-fixed-rate": 8,
    //     "booking-fee-fixed-rate": 10,
    //     "booking-fee-percentage": 2,
    //     "commission-fee-percentage": 3,
    //     "ticket-min-percentage-decider": 100,
    //     "commission-fee-decider": 100
    //   },
    //   "message": "Settings retrieved successfully"
    // }

    // console.log("platformPricingSettings: ", JSON.stringify(platformPricingSettings, null, 2));

    const [price, setPrice] = useState(350)
    const [qty, setQty] = useState(1)
    const [passPlatform, setPassPlatform] = useState(false)
    const [audience, setAudience] = useState("normalUser")

    const res = compute(price, qty, passPlatform, audience)

    function inr(n) {
        if (!isFinite(n)) return "₹0"
        // Show up to 2 decimals for fractional paise cases
        return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(
            Math.round(n * 100) / 100,
        )
    }

    function calcBookingFee(price) {
        if (price <= 0) return 0

        if (price >= (platformPricingSettings["ticket-min-percentage-decider"] || 100) && price <= (platformPricingSettings["ticket-booking-fee-decider"] || 500)) {
            return platformPricingSettings["booking-fee-fixed-rate"] || 10
        }

        return price * ((platformPricingSettings["booking-fee-percentage"] || 2) / 100)
    }

    function calcPlatformFee(price, audience = "normalUser") {
        if (price <= 0) return 0

        // End-user (default) rules
        if (audience === "normalUser") {
            if (price < (platformPricingSettings["commission-fee-decider"] || 100)) {
                return price * ((platformPricingSettings["commission-fee-percentage"] || 3) / 100)
            }

            return price * ((platformPricingSettings["commission-fee-percentage"] || 3) / 100) + (platformPricingSettings["commission-fee-fixed-rate"] || 8)
        }

        // Business rules: 2% under ₹100; 2% + ₹5 for ₹100+
        if (price < (platformPricingSettings["commission-fee-decider"] || 100)) {
            return price * ((platformPricingSettings["booking-fee-percentage"] || 2) / 100)
        }

        return price * ((platformPricingSettings["booking-fee-percentage"] || 2) / 100) + 5
    }

    function compute(price, qty, passPlatformToBuyer, audience) {
        const p = Math.max(0, price)
        const q = Math.max(0, Math.floor(qty || 0))

        const booking = calcBookingFee(p)
        const platform = calcPlatformFee(p, audience)

        const buyerPer = p + booking + (passPlatformToBuyer ? platform : 0)
        const creatorPer = p - (passPlatformToBuyer ? 0 : platform)
        const platformPer = booking + platform

        return {
            bookingFeePer: booking,
            platformFeePer: platform,
            buyerPaysPer: buyerPer,
            creatorReceivesPer: creatorPer,
            platformEarnsPer: platformPer,
            buyerPaysTotal: buyerPer * q,
            creatorReceivesTotal: creatorPer * q,
            platformEarnsTotal: platformPer * q,
        }
    }

    useEffect(() => {
        if (!platformPricingSettings || Object.keys(platformPricingSettings).length === 0) {
            dispatch(getPlatformPricing())
        }
    }, [dispatch, platformPricingSettings])

    const options = [
        {
            label: "Normal User",
            value: 'normalUser'
        },
        {
            label: 'Business',
            value: 'business'
        }
    ]

    return (
        <div className="page-view">
            <div className="page-wrapper">

                <div className="pricing-container">
                    <div className="pricing-wrapper">

                        <div className="pricing-icon-container">
                            <AdminPanelSettings color="tertiary" sx={{ fontSize: '3rem' }} />
                        </div>

                        <div className="pricing-content-container">

                            <header className="pricing-header">
                                <H2 fontWeight={700}>
                                    Pricing
                                </H2>

                                <Autocomplete
                                    disablePortal
                                    id="audienceType"
                                    options={options}
                                    defaultValue={options[0]}
                                    getOptionLabel={(option) => option.label}
                                    disableClearable
                                    onChange={(event, newValue) => {
                                        setAudience(newValue?.value || "normalUser")
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Pricing For"
                                        />
                                    )}
                                />
                            </header>

                            {audience === 'normalUser' && (
                                <div className="pricing-detail-body">

                                    <div className="pricing-detail-transparent">
                                        <H5 color="var(--secondary-color)" fontWeight={700}>
                                            Transparent, simple fees. No extra payment gateway markup—ever.
                                        </H5>

                                        <ul className="pricing-list">
                                            <Body2>
                                                <li>
                                                    All fees are per ticket and exclude GST.
                                                </li>
                                            </Body2>
                                            <Body2>
                                                <li>
                                                    No fees on free tickets.
                                                </li>
                                            </Body2>
                                            <Body2>
                                                <li>
                                                    Creators may choose to pass platform fees to buyers. When enabled, buyers pay both booking and platform fees.
                                                </li>
                                            </Body2>
                                        </ul>
                                    </div>

                                </div>
                            )}

                            {audience === 'normalUser' && (
                                <div className="pricing-details">

                                    <div className="pricing-detail-transparent fees-rule-min-height">

                                        <H5 color="var(--secondary-color)" fontWeight={700}>
                                            Fee Rules
                                        </H5>

                                        <div className="fee-rules-container">

                                            <div className="fees-rule">
                                                <Body1>
                                                    Booking Fees (paid by buyers)
                                                </Body1>

                                                <ul className="pricing-list">
                                                    <Body2>
                                                        <li>
                                                            No fees on free tickets.
                                                        </li>
                                                    </Body2>
                                                    <Body2>
                                                        <li>
                                                            ₹{platformPricingSettings["booking-fee-fixed-rate"] || 10} per ticket for ticket prices between ₹{platformPricingSettings["ticket-min-percentage-decider"] || 100} and ₹{platformPricingSettings["ticket-booking-fee-decider"] || 500} (inclusive).
                                                        </li>
                                                    </Body2>
                                                    <Body2>
                                                        <li>
                                                            {platformPricingSettings["booking-fee-percentage"] || 2}% per ticket for ticket prices less than ₹{platformPricingSettings["ticket-min-percentage-decider"] || 100} or greater than ₹{platformPricingSettings["ticket-booking-fee-decider"] || 500}.
                                                        </li>
                                                    </Body2>
                                                </ul>
                                            </div>

                                            <div className="fees-rule">
                                                <Body1>
                                                    Platform Fees (for creators – Standard rates)
                                                </Body1>

                                                <ul className="pricing-list">
                                                    <Body2>
                                                        <li>
                                                            No fees on free tickets.
                                                        </li>
                                                    </Body2>
                                                    <Body2>
                                                        <li>
                                                            {platformPricingSettings["commission-fee-percentage"] || 3}% per ticket for ticket prices less than ₹{platformPricingSettings["commission-fee-decider"] || 100}.
                                                        </li>
                                                    </Body2>
                                                    <Body2>
                                                        <li>
                                                            {platformPricingSettings["commission-fee-percentage"] || 3}% + ₹{platformPricingSettings["commission-fee-fixed-rate"] || 8} per ticket for ticket prices ₹{platformPricingSettings["commission-fee-decider"] || 100} or more.
                                                        </li>
                                                    </Body2>
                                                    <Body2>
                                                        <li>
                                                            Creators can choose to pass platform fees on to buyers. If enabled, buyers will pay both booking and platform fees.
                                                        </li>
                                                    </Body2>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="right-side-details">

                                        <div className="fees-calculator">

                                            <div className="fees-calculator-title">
                                                <H6 fontWeight={700} color="var(--secondary-color)">
                                                    Interactive calculator
                                                </H6>
                                                <Body3>
                                                    Model buyer vs. creator scenarios and totals per quantity
                                                </Body3>
                                            </div>

                                            <div className="fees-calculator-inputs">
                                                <TextField
                                                    label="Ticket Price"
                                                    type="number"
                                                    helperText="Set to 0 for free tickets."
                                                    value={Number.isFinite(price) ? price : 0}
                                                    onChange={(e) => setPrice(Number.parseFloat(e.target.value || "0"))}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': { maxWidth: '160px' },
                                                    }}
                                                    slotProps={{
                                                        input: {
                                                            startAdornment: (
                                                                <InputAdornment
                                                                    position="start"
                                                                >
                                                                    ₹
                                                                </InputAdornment>
                                                            ),
                                                            inputProps: {
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                min: 0
                                                            }
                                                        },
                                                    }}
                                                />

                                                <TextField
                                                    label="Quantity"
                                                    type="number"
                                                    value={Number.isFinite(qty) ? qty : 0}
                                                    onChange={(e) => setQty(Number.parseInt(e.target.value || "0", 10))}
                                                    defaultValue={1}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': { maxWidth: '160px' },
                                                    }}
                                                    slotProps={{
                                                        input: {
                                                            inputProps: {
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                min: 0
                                                            }
                                                        },
                                                    }}
                                                />
                                            </div>

                                            <div className="fees-toggle-container">
                                                <IOSSwitch
                                                    checked={passPlatform}
                                                    onChange={(e) => setPassPlatform(e.target.checked)}
                                                    defaultChecked
                                                />
                                                <div className="fees-toggle-content">
                                                    <Body2>
                                                        Pass platform fee to buyer
                                                    </Body2>
                                                    <Body3>
                                                        Buyer pays booking + platform fees; creator keeps full ticket price.
                                                    </Body3>
                                                </div>
                                            </div>

                                            <div className="calculator-result-container">

                                                <div className="per-ticket-fees-container">
                                                    <div className="per-ticket-fee-title">
                                                        <Body2>
                                                            Per ticket
                                                        </Body2>
                                                    </div>

                                                    <div className="per-ticket-fee-details">

                                                        <div className="per-ticket-fee-amount">

                                                            <div className="per-ticket-fees">
                                                                <div className="fees-label">
                                                                    <Body3>
                                                                        Booking Fee
                                                                    </Body3>
                                                                </div>
                                                                <div className="fees-amount">
                                                                    <Body3 fontWeight={500}>
                                                                        {inr(res.bookingFeePer)}
                                                                    </Body3>
                                                                </div>
                                                            </div>

                                                            <div className="per-ticket-fees">
                                                                <div className="fees-label">
                                                                    <Body3>
                                                                        Platform Fee
                                                                    </Body3>
                                                                </div>
                                                                <div className="fees-amount">
                                                                    <Body3 fontWeight={500}>
                                                                        {inr(res.platformFeePer)}
                                                                    </Body3>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <Divider
                                                            sx={{
                                                                borderColor: 'var(--divider-color)',
                                                                alignSelf: 'stretch',
                                                            }}
                                                        />

                                                        <div className="per-ticket-fee-amount">

                                                            <div className="per-ticket-fees">
                                                                <div className="fees-label">
                                                                    <Body3>
                                                                        Buyer Pays
                                                                    </Body3>
                                                                </div>
                                                                <div className="fees-amount">
                                                                    <Body3 fontWeight={500}>
                                                                        {inr(res.buyerPaysPer)}
                                                                    </Body3>
                                                                </div>
                                                            </div>

                                                            <div className="per-ticket-fees">
                                                                <div className="fees-label">
                                                                    <Body3>
                                                                        Creator Receives
                                                                    </Body3>
                                                                </div>
                                                                <div className="fees-amount">
                                                                    <Body3 fontWeight={500}>
                                                                        {inr(res.creatorReceivesPer)}
                                                                    </Body3>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="total-fees-container">

                                                    <div className="per-ticket-fee-title">
                                                        <Body2>
                                                            Totals (× {qty || 0})
                                                        </Body2>
                                                    </div>

                                                    <div className="buyer-creator-pays-container">

                                                        <div className="buyer-creator-pays">
                                                            <Body3>
                                                                Buyer Pays Total
                                                            </Body3>

                                                            <div className="buyer-creator-pays-amount">
                                                                <H5 color="var(--secondary-color)" fontWeight={600}>
                                                                    {inr(res.buyerPaysTotal)}
                                                                </H5>
                                                            </div>
                                                        </div>
                                                        <div className="buyer-creator-pays">
                                                            <Body3>
                                                                Creator Receives Total
                                                            </Body3>

                                                            <div className="buyer-creator-pays-amount">
                                                                <H5 color="var(--secondary-color)" fontWeight={600}>
                                                                    {inr(res.creatorReceivesTotal)}
                                                                </H5>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>

                                            <div className="fees-calculator">
                                                <Body3>
                                                    All fees exclude GST. No extra payment gateway fees are charged by the platform.
                                                </Body3>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                            )}

                            {audience === 'business' && (
                                <ComingSoon />
                            )}

                            {audience === 'normalUser' && (
                                <div className="pricing-detail-transparent">
                                    <H5 color="var(--secondary-color)" fontWeight={700}>
                                        Note
                                    </H5>

                                    <Body2>
                                        Everything you need to know at a glance
                                    </Body2>

                                    <ul className="pricing-list">
                                        <Body2>
                                            <li>
                                                All prices exclude GST.
                                            </li>
                                        </Body2>
                                        <Body2>
                                            <li>
                                                No extra payment gateway (PG) fees are charged by the platform.
                                            </li>
                                        </Body2>
                                        <Body2>
                                            <li>
                                                Fees are applied per ticket at the time of purchase.
                                            </li>
                                        </Body2>
                                        <Body2>
                                            <li>
                                                Free tickets incur no fees for both buyers and creators.
                                            </li>
                                        </Body2>
                                    </ul>
                                </div>
                            )}

                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default Pricing

const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 4,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#f5f5f5',
            '& + .MuiSwitch-track': {
                backgroundColor: 'var(--secondary-color)',
                opacity: 1,
                border: 0,
                ...theme.applyStyles('dark', {
                    backgroundColor: 'var(--secondary-color)',
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid var(--quaternary-color)',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
            ...theme.applyStyles('dark', {
                color: theme.palette.grey[600],
            }),
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
            ...theme.applyStyles('dark', {
                opacity: 0.3,
            }),
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 18,
        height: 18,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: 'var(--gray-main-color)',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
        ...theme.applyStyles('dark', {
            backgroundColor: '#39393D',
        }),
    },
}));

const ComingSoon = () => {
    const styles = {
        container: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "4rem 2rem",
            minHeight: "400px",
            // background: "linear-gradient(135deg, #11203a 0%, #1a2f4a 100%)",
            position: "relative",
            overflow: "hidden",
            width: "100%",
        },
        backgroundShapes: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
        },
        circle1: {
            position: "absolute",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "linear-gradient(45deg, #F8A21E, #26C1E8)",
            top: "-100px",
            right: "-100px",
            animation: "float 6s ease-in-out infinite",
        },
        circle2: {
            position: "absolute",
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: "linear-gradient(45deg, #26C1E8, #F8A21E)",
            bottom: "-75px",
            left: "-75px",
            animation: "float 8s ease-in-out infinite reverse",
        },
        content: {
            textAlign: "center",
            zIndex: 2,
            position: "relative",
        },
        iconContainer: {
            width: "80px",
            height: "80px",
            margin: "0 auto 2rem",
            // background: "linear-gradient(135deg, #F8A21E, #26C1E8)",
            background: 'var(--tertiary-light-bg-color)',
            border: '1px solid var(--tertiary-color)',
            borderRadius: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "pulse 2s ease-in-out infinite",
        },
        icon: {
            width: "40px",
            height: "40px",
            fill: "white",
        },
        title: {
            fontSize: "2.5rem",
            fontWeight: "700",
            background: "linear-gradient(135deg, #F8A21E, #26C1E8)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            marginBottom: "0.5rem",
            letterSpacing: "2px",
        },
        subtitle: {
            color: "#94A3B8",
            fontSize: "1.1rem",
            fontWeight: "400",
        },
        keyframes: `
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(180deg); }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
    `,
    }

    return (
        <>
            <style>{styles.keyframes}</style>
            <div style={styles.container}>
                {/* <div style={styles.backgroundShapes}>
                    <div style={styles.circle1}></div>
                    <div style={styles.circle2}></div>
                </div> */}

                <div style={styles.content}>
                    <div style={styles.iconContainer}>
                        <AccessTime color="tertiary" sx={{ fontSize: '2.5rem' }} />
                    </div>

                    <H2 color="var(--secondary-color)" fontWeight={700}>
                        COMING SOON
                    </H2>
                    <Body1>
                        We're working hard to launch this feature. Stay tuned!
                    </Body1>
                </div>
            </div>
        </>
    )
}